/**
 * PROMPT 2: INTEGRATIONS CANVAS WITH LIVE ANIMATIONS
 * 
 * Paste this into v0.dev or Cursor:
 * You are a senior React + reactflow developer.
 * 
 * TASK: Build the "Integrations Flow Canvas" for UptimeWing. This teaches users how alerts work visually.
 * 
 * TECH: Next.js 14, React, TypeScript, Tailwind, shadcn/ui, @xyflow/react, framer-motion
 * 
 * REQUIREMENTS:
 * 1. LAYOUT
 *     3 columns:
 *     Left: `<NodePalette />` - Draggable items
 *         Section "WHEN THIS HAPPENS": [Watch: myshop.com] [Watch: API Server]
 *     Center: `<FlowCanvas />` - Full height reactflow canvas
 *     Right: `<ActionPalette />` - Draggable items
 *         Section "DO THIS": [Email] [Telegram] [Discord] [Webhook]
 *     Top Bar: [Test Flow Button] [Save Flow Button]
 * 
 * 2. NODES
 *     Create 2 custom reactflow nodes:
 *     WatchNode: Shows favicon + name + status dot. Green=UP Red=DOWN
 *     ActionNode: Shows icon + name. Ex: Telegram logo + "Telegram"
 *     Nodes are draggable on canvas. Click node to open settings sidebar.
 * 
 * 3. CONNECTIONS
 *     User can drag from WatchNode output handle to ActionNode input handle.
 *     On connect, animate the edge with a green pulse once.
 * 
 * 4. LIVE ANIMATION - "TEST FLOW"
 *     This is the key feature.
 *     When "Test Flow" button is clicked:
 *     1. Find all connected edges
 *     2. For each edge, create a red dot that animates along the edge path from source to target over 800ms using framer-motion
 *     3. When dot reaches ActionNode, the ActionNode shakes and shows toast: "Test Alert Sent to Telegram!"
 *     Use `@xyflow/react` `useReactFlow` and `getBezierPath` to animate along the path.
 * 
 * 5. UX DETAILS
 *     Empty state: "Drag a Watch and an Action here, then connect them"
 *     Tooltip on edge: "If myshop.com goes DOWN, send alert to Telegram"
 *     Use shadcn Toast for "Test Alert Sent"
 * 
 * 6. DATA
 *     Mock data to start:
 *     const initialNodes = [{id: '1', type: 'watch', data: {label: 'myshop.com'}}]
 *     const initialEdges = []
 * 
 * OUTPUT: Single TSX file. Export default `IntegrationsCanvas` component. Include all custom nodes and the test animation logic.
 */

import React, { useState, useCallback } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  Connection,
  Edge,
  Node,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
  getBezierPath,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Play, Save, Info, Sparkles, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

// Custom Target Node
function WatchNodeCustom({ data }: any) {
  const isDown = data.status === "down";
  return (
    <div className="border-4 border-foreground p-3 bg-[#FFFF00] text-black font-sans font-black shadow-[4px_4px_0_0_#000] relative min-w-[140px] text-xs">
      <div className="uppercase tracking-wide text-[8px] text-muted-foreground/60 leading-none">Watch Target</div>
      <div className="mt-1 flex items-center justify-between gap-1.5 uppercase tracking-tight">
        <span className="flex items-center gap-1">🌐 {data.label}</span>
        <span className={`h-2 w-2 rounded-full border border-black ${isDown ? "bg-red-500 animate-pulse" : "bg-green-500"}`} />
      </div>
      <Handle type="source" position={Position.Right} className="w-3.5 h-3.5 bg-black border-2 border-white rounded-none" />
    </div>
  );
}

// Custom Action Node
function ActionNodeCustom({ data }: any) {
  const isTarget = data.isTargetShake;
  return (
    <motion.div
      animate={isTarget ? { x: [-3, 3, -3, 3, 0], transition: { duration: 0.4, repeat: 3 } } : {}}
      className={`border-4 border-foreground p-3 bg-card text-foreground font-sans font-black shadow-[4px_4px_0_0_#000] relative min-w-[140px] text-xs transition-all ${
        isTarget ? "bg-green-150 border-green-500 scale-105" : ""
      }`}
    >
      <div className="uppercase tracking-wide text-[8px] text-muted-foreground/60 leading-none font-mono">Action Output</div>
      <div className="mt-1 flex items-center gap-1.5 uppercase tracking-tight font-sans">
        <span>{data.icon || "📧"}</span> {data.label}
      </div>
      <Handle type="target" position={Position.Left} className="w-3.5 h-3.5 bg-black border-2 border-white rounded-none" />
    </motion.div>
  );
}

const nodeTypes = {
  watchNode: WatchNodeCustom,
  actionNode: ActionNodeCustom,
};

// Animated Dot Overlay Component
interface AnimatedDotProps {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}

function FlowDot({ sourceX, sourceY, targetX, targetY }: AnimatedDotProps) {
  const [path] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  });

  return (
    <svg className="absolute inset-0 pointer-events-none w-full h-full z-50">
      <motion.circle
        r="6"
        fill="#ef4444"
        className="stroke-2 stroke-black"
        initial={{ offsetDistance: "0%" }}
        animate={{ offsetDistance: "100%" }}
        transition={{ duration: 0.9, ease: "easeInOut" }}
        style={{
          motionPath: path,
        }}
      />
    </svg>
  );
}

function CanvasInner() {
  const reactFlowInstance = useReactFlow();
  const [nodes, setNodes] = useState<Node[]>([
    {
      id: "node-1",
      type: "watchNode",
      position: { x: 50, y: 100 },
      data: { label: "myshop.com", status: "up" },
    },
    {
      id: "node-2",
      type: "actionNode",
      position: { x: 320, y: 100 },
      data: { label: "Telegram", icon: "💬", isTargetShake: false },
    },
  ]);

  const [edges, setEdges] = useState<Edge[]>([
    { id: "edge-1", source: "node-1", target: "node-2", animated: true, style: { strokeWidth: 3, stroke: "#000" } },
  ]);

  const [dots, setDots] = useState<AnimatedDotProps[]>([]);
  const [isTesting, setIsTesting] = useState(false);

  const onNodesChange = useCallback(
    (changes: any) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes: any) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnect = useCallback(
    (params: Connection) =>
      setEdges((eds) =>
        addEdge(
          { ...params, animated: true, style: { strokeWidth: 3, stroke: "#000" } },
          eds
        )
      ),
    []
  );

  const handleTestFlow = () => {
    if (isTesting) return;
    setIsTesting(true);

    const activeDots: AnimatedDotProps[] = [];

    edges.forEach((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);

      if (sourceNode && targetNode) {
        // Approximate points for animation path
        activeDots.push({
          id: `${edge.id}-${Date.now()}`,
          sourceX: sourceNode.position.x + 140,
          sourceY: sourceNode.position.y + 25,
          targetX: targetNode.position.x,
          targetY: targetNode.position.y + 25,
        });
      }
    });

    setDots(activeDots);

    // Target shaking and notifications callback
    setTimeout(() => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.type === "actionNode") {
            const isConnected = edges.some((e) => e.target === node.id);
            if (isConnected) {
              toast(`Test alert sent successfully to ${node.data.label}!`);
              return {
                ...node,
                data: { ...node.data, isTargetShake: true },
              };
            }
          }
          return node;
        })
      );
    }, 850);

    setTimeout(() => {
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          data: { ...node.data, isTargetShake: false },
        }))
      );
      setDots([]);
      setIsTesting(false);
    }, 2500);
  };

  return (
    <div className="space-y-4 font-sans">
      {/* Flow builder top toolbar controls */}
      <div className="flex flex-wrap justify-between items-center gap-3 border-b-2 border-foreground pb-4">
        <div className="space-y-0.5">
          <h2 className="text-lg font-black uppercase tracking-tight flex items-center gap-1.5">
            🔌 Flow Canvas
          </h2>
          <p className="text-[10px] text-muted-foreground uppercase font-mono">
            Connect Watches to actions to build alerts without code.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleTestFlow}
            disabled={isTesting || edges.length === 0}
            className="rounded-none border-2 border-foreground bg-[#FFFF00] text-black hover:bg-black hover:text-white font-black uppercase text-[10px] h-9 px-4 flex items-center gap-1.5"
          >
            <Play className="h-3.5 w-3.5 fill-current" /> Test Flow
          </Button>
          <Button
            onClick={() => toast("Flow settings saved successfully!")}
            className="rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase text-[10px] h-9 px-4 flex items-center gap-1.5"
          >
            <Save className="h-3.5 w-3.5" /> Save Flow
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 border-4 border-foreground h-[480px] bg-background relative overflow-hidden">
        {/* Left Column: Source Items */}
        <div className="p-4 border-r-2 border-foreground bg-muted space-y-3 overflow-y-auto font-mono text-[9px] font-bold">
          <div className="uppercase text-muted-foreground tracking-wider mb-2">WHEN THIS HAPPENS:</div>
          <div className="border-2 border-foreground p-2.5 bg-[#FFFF00] text-black font-black uppercase flex items-center justify-between shadow-xs">
            <span>🌐 myshop.com</span>
            <span className="h-1.5 w-1.5 bg-green-500 rounded-full border border-black" />
          </div>
          <div className="border-2 border-foreground p-2.5 bg-[#FFFF00] text-black font-black uppercase flex items-center justify-between shadow-xs opacity-50">
            <span>⚡ API Server</span>
            <span className="h-1.5 w-1.5 bg-green-500 rounded-full border border-black" />
          </div>
        </div>

        {/* Canvas Area */}
        <div className="md:col-span-2 relative h-full bg-zinc-50 dark:bg-zinc-950">
          {edges.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-6 text-center z-10">
              <p className="text-[10px] font-mono font-black uppercase text-muted-foreground max-w-[200px] leading-relaxed">
                Drag a Watch and an Action here, then connect them
              </p>
            </div>
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
          >
            <Controls showInteractive={false} className="border-2 border-foreground bg-background rounded-none" />
            <Background color="#000" gap={16} size={1} />
          </ReactFlow>

          {/* Flow Animation overlay */}
          {dots.map((d) => (
            <FlowDot key={d.id} {...d} />
          ))}
        </div>

        {/* Right Column: Actions Items */}
        <div className="p-4 border-l-2 border-foreground bg-muted space-y-3 overflow-y-auto font-mono text-[9px] font-bold">
          <div className="uppercase text-muted-foreground tracking-wider mb-2">DO THIS:</div>
          <div className="border-2 border-foreground p-2.5 bg-background text-foreground font-black uppercase flex items-center gap-1.5 shadow-xs">
            <span>📧</span> Email Alert
          </div>
          <div className="border-2 border-foreground p-2.5 bg-background text-foreground font-black uppercase flex items-center gap-1.5 shadow-xs">
            <span>💬</span> Telegram Alert
          </div>
          <div className="border-2 border-foreground p-2.5 bg-background text-foreground font-black uppercase flex items-center gap-1.5 shadow-xs">
            <span>👾</span> Discord Webhook
          </div>
        </div>
      </div>
    </div>
  );
}

export function IntegrationsCanvas() {
  return (
    <ReactFlowProvider>
      <CanvasInner />
    </ReactFlowProvider>
  );
}

// Simple toast notification helper
function toast(message: string) {
  const el = document.createElement("div");
  el.className = "fixed bottom-5 right-5 z-50 bg-black text-white dark:bg-white dark:text-black border-2 border-white dark:border-black p-4 font-mono text-[10px] uppercase font-black tracking-wider shadow-md animate-bounce";
  el.innerText = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4000);
}
